import React, { useEffect, useState } from 'react';
import { Box, Container, Header, SpaceBetween, CopyToClipboard, LiveRegion, Popover } from '@amzn/awsui-components-core';
import LoadingBar from '@amzn/awsui-chat-components/loading-bar';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { fetchPageSummary, SummaryResponse } from 'src/api/app-sync-events-service';
import { useEventsApiContext } from 'src/components/context/EventsApiContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { processFiltersForAISummary } from 'src/utilities/CommonUtilities';
import { v4 as uuidv4 } from 'uuid';
import { FeedbackButtons } from './FeedbackButtons';
import { ApiErrorHandler } from 'src/utilities/ApiErrorHandler';
import styles from './ai-summary-components.module.scss';
import { logger } from 'src/logger';

interface AISummaryBoxProps {
  pageType: string;
  filters: string;
  title?: string;
  statusKey?: string;
  statusValue?: string;
  aggregatedData: string;
  originalOptions?: any;
}

export const AISummaryBox: React.FC<AISummaryBoxProps> = ({
  pageType,
  filters,
  title = 'Summary',
  statusKey,
  statusValue = 'finished',
  aggregatedData,
  originalOptions
}) => {
  const [summary, setSummary] = useState<string>('');
  const [summaryId, setSummaryId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const eventsApiContext = useEventsApiContext();
  const { Alias } = useAuth();

  useEffect(() => {
    // Only fetch when status becomes the expected value
    setShouldFetch(!statusKey || !statusValue || statusKey === statusValue);
  }, [statusKey, statusValue]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!shouldFetch || !pageType) return;

      const currentFilters = processFiltersForAISummary(filters, originalOptions);
      const cacheKey = `${pageType}-${currentFilters}`;

      // Cancel any existing request for this pageType before proceeding
      const wasCanceled = eventsApiContext.cancelPageRequest(pageType);
      if (wasCanceled) {
        logger.info(`Canceled previous request for pageType: ${pageType}`);
      }

      const cachedEntry = eventsApiContext.getCacheEntry(cacheKey);
      if (cachedEntry && 'summary' in cachedEntry) {
        setSummary(cachedEntry.summary);
        setSummaryId(cachedEntry.summaryId);
        setError(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        setSummary('');
        if (!eventsApiContext) {
          console.error('eventsApiContext is not available. Make sure the component is wrapped in EventsApiContextProvider');
          setError(true);
          setSummary('Unable to load summary: missing context');
          setLoading(false);
          return;
        }

        const sessionId = uuidv4();
        const { message, summaryId: receivedSummaryId } = await fetchPageSummary(
          sessionId,
          pageType,
          currentFilters,
          aggregatedData,
          eventsApiContext,
          Alias
        );
        setSummary(message);
        setSummaryId(receivedSummaryId);

        eventsApiContext.setCacheEntry(cacheKey, {
          summary: message,
          summaryId: receivedSummaryId
        });
      } catch (error) {
        console.error('Error fetching events summary:', error);
        const errorMessage = ApiErrorHandler.handleError(error as Error);
        setSummary(errorMessage);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (shouldFetch) {
      fetchSummary();
    }
  }, [shouldFetch, pageType, filters, aggregatedData]);

  return (
    <Box padding="m">
      <Container header={<Header variant="h2">{title}</Header>}>
        <SpaceBetween direction="vertical" size="xs">
          {loading ? (
            <Box padding="l">
              <LiveRegion>
                <SpaceBetween direction="vertical" size="s">
                  <Box color="text-body-secondary">Generating the summary</Box>
                  <div className="ai-summary-loading-bar">
                    <LoadingBar variant="gen-ai" />
                  </div>
                </SpaceBetween>
              </LiveRegion>
            </Box>
          ) : error ? (
            <Box textAlign="center" padding="l" color="text-status-error">
              <Box variant="p">Unable to load summary. Please try again later.</Box>
            </Box>
          ) : (
            <>
              <Box padding={{ horizontal: 's', vertical: 'xs' }}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(summary) }} />
              </Box>
              {/* Compact feedback and copy buttons in one line */}
              {summary && summaryId && (
                <Box padding={{ horizontal: 's', top: 'xxs' }}>
                  <div className={styles.feedbackCopyLayout}>
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      <Box fontSize="body-s" color="text-body-secondary">
                        Was this helpful?
                      </Box>
                      <FeedbackButtons summaryId={summaryId} userId={Alias} />
                    </SpaceBetween>
                    <CopyToClipboard
                      copyButtonAriaLabel="Copy Summary"
                      copyErrorText="Failed to copy"
                      copySuccessText="Summary copied"
                      textToCopy={summary}
                      variant="icon"
                    />
                  </div>
                </Box>
              )}
            </>
          )}
        </SpaceBetween>
      </Container>
    </Box>
  );
};
